/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * Attest (v1_465) — the director's one screen at /attest?token=...
 *
 * A named director confirms (or declines to confirm) that the person
 * completing onboarding is authorised to act for the company. Behind the
 * OTP-1 gate: nothing renders before a 6-digit code emailed to the director's
 * own address is verified. One decision, ever; the server enforces it.
 */

import { useState, useEffect } from "react";

const PAGE = { minHeight: "100vh", background: "#f3f4f6", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const CARD = { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "36px 40px", width: 480, maxWidth: "94%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" };

export default function Attest() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [done, setDone] = useState(null); // 'confirmed' | 'declined'
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [otpGate, setOtpGate] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0);

  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);

  const otpRequestCode = async (tok) => {
    setOtpError(null);
    try {
      const r = await fetch("/api/otp-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "attest", token: tok }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.sent) { setOtpGate(g => ({ ...(g || { token: tok }), maskedEmail: d.maskedEmail || (g && g.maskedEmail) })); setOtpResendIn(30); }
      else if (!r.ok) setOtpError(d.error || "The code could not be sent. Please try again shortly.");
    } catch (_) { setOtpError("The code could not be sent. Please check your connection and try again."); }
  };

  const load = async (tok) => {
    try {
      const r = await fetch("/api/attestation", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "data", token: tok }) });
      const d = await r.json().catch(() => null);
      if (r.status === 401 && d && d.otpRequired) { setOtpGate(g => g || { token: tok, maskedEmail: null }); otpRequestCode(tok); setLoading(false); return; }
      if (!r.ok || !d || !d.success) { setError((d && d.error) || "This request could not be loaded."); setLoading(false); return; }
      if (d.status !== "pending") setDone(d.status);
      setData({ ...d, token: tok });
      setLoading(false);
    } catch (_) { setError("This request could not be loaded. Please check your connection and try again."); setLoading(false); }
  };

  const otpVerifyCode = async () => {
    if (!otpGate || otpBusy) return;
    setOtpBusy(true); setOtpError(null);
    try {
      const r = await fetch("/api/otp-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "attest", token: otpGate.token, code: otpCode.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) { const tok = otpGate.token; setOtpGate(null); setOtpCode(""); setLoading(true); load(tok); }
      else { setOtpError(d.error || "That code did not verify. Please try again."); if (d.requestNew) setOtpCode(""); }
    } catch (_) { setOtpError("Verification failed. Please check your connection and try again."); }
    finally { setOtpBusy(false); }
  };

  const decide = async (decision) => {
    if (deciding || !data) return;
    setDeciding(true); setError(null);
    try {
      const r = await fetch("/api/attestation", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "decide", token: data.token, decision, note: note.trim() || undefined }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) setDone(d.status);
      else setError(d.error || "Your response could not be recorded. Please try again.");
    } catch (_) { setError("Your response could not be recorded. Please check your connection and try again."); }
    finally { setDeciding(false); setConfirmingDecline(false); }
  };

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) { setError("This link is not valid."); setLoading(false); return; }
    load(token);
  }, []); // one-shot bootstrap; verify re-runs load directly

  if (otpGate) return (
    <div style={PAGE}>
      <div style={{ ...CARD, textAlign: "center", width: 440 }}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Confirm it's you</div>
        <div style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.7, marginBottom: 22 }}>
          This request is personal to you. We have emailed a 6-digit code to {otpGate.maskedEmail ? <span style={{ color: "#0d9488", fontWeight: 600 }}>{otpGate.maskedEmail}</span> : "your address"}. Enter it below to continue.
        </div>
        <input
          value={otpCode}
          onChange={e => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(null); }}
          onKeyDown={e => { if (e.key === "Enter" && otpCode.length === 6) otpVerifyCode(); }}
          inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="••••••"
          style={{ width: "100%", boxSizing: "border-box", background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 10, color: "#111827", fontSize: 28, fontWeight: 600, letterSpacing: "12px", textAlign: "center", padding: "13px 0 13px 12px", outline: "none", marginBottom: 14 }}
        />
        {otpError && <div style={{ fontSize: 12.5, color: "#dc2626", lineHeight: 1.6, marginBottom: 12 }}>{otpError}</div>}
        <button onClick={otpVerifyCode} disabled={otpBusy || otpCode.length !== 6}
          style={{ width: "100%", background: otpBusy || otpCode.length !== 6 ? "#99d6d0" : "#0d9488", color: "#ffffff", border: "none", padding: "13px 0", borderRadius: 8, fontWeight: 700, fontSize: 14.5, cursor: otpBusy || otpCode.length !== 6 ? "default" : "pointer", marginBottom: 14 }}
        >{otpBusy ? "Verifying..." : "Continue"}</button>
        <button onClick={() => otpRequestCode(otpGate.token)} disabled={otpResendIn > 0}
          style={{ background: "none", border: "none", color: otpResendIn > 0 ? "#9ca3af" : "#0d9488", fontSize: 13, cursor: otpResendIn > 0 ? "default" : "pointer", textDecoration: "underline" }}
        >{otpResendIn > 0 ? `Send a new code (${otpResendIn}s)` : "Send a new code"}</button>
        <div style={{ marginTop: 18, fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>The code expires after 10 minutes.</div>
      </div>
    </div>
  );

  if (loading) return <div style={PAGE}><div style={{ color: "#6b7280", fontSize: 14 }}>Loading...</div></div>;

  if (error && !data) return (
    <div style={PAGE}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ color: "#dc2626", marginBottom: 8, fontWeight: 600, fontSize: 15 }}>{error}</div>
        <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.6 }}>If you believe this is in error, please contact the compliance team that sent the request.</div>
      </div>
    </div>
  );

  if (done) return (
    <div style={PAGE}>
      <div style={{ ...CARD, textAlign: "center" }}>
        <div style={{ fontSize: 34, marginBottom: 10 }}>{done === "confirmed" ? "✓" : "—"}</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{done === "confirmed" ? "Authority confirmed" : "Response recorded"}</div>
        <div style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.7 }}>
          {done === "confirmed"
            ? `Thank you. Your confirmation has been recorded on the compliance file for ${data?.companyName || "the company"} and the team has been notified. You can close this page.`
            : `Your response has been recorded and the compliance team has been notified. They may contact you to resolve the question of authority. You can close this page.`}
        </div>
      </div>
    </div>
  );

  return (
    <div style={PAGE}>
      <div style={CARD}>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{data.firmName} · Compliance onboarding</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Confirmation of authority</div>
        <div style={{ fontSize: 14, lineHeight: 1.8, marginBottom: 18, color: "#374151" }}>
          <strong>{data.completerName}</strong> is completing the compliance onboarding of <strong>{data.companyName || "your company"}</strong> with {data.firmName}. As a director, you are asked to confirm the statement below.
        </div>
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 18px", fontSize: 13.5, lineHeight: 1.8, color: "#111827", marginBottom: 16 }}>
          I, {data.directorName}, in my capacity as a director of {data.companyName || "the company"}, confirm that {data.completerName} is authorised to act on behalf of the company in its compliance onboarding with {data.firmName}, including providing information and documents and making declarations in the course of that onboarding.
        </div>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value.slice(0, 1000))}
          placeholder="Optional note (for example, any limits on the authorisation)"
          rows={2}
          style={{ width: "100%", boxSizing: "border-box", background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", color: "#111827", resize: "vertical", marginBottom: 16 }}
        />
        {error && <div style={{ fontSize: 12.5, color: "#dc2626", lineHeight: 1.6, marginBottom: 12 }}>{error}</div>}
        {!confirmingDecline ? (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => decide("confirm")} disabled={deciding}
              style={{ flex: 1.4, background: deciding ? "#99d6d0" : "#0d9488", color: "#ffffff", border: "none", padding: "13px 0", borderRadius: 8, fontWeight: 700, fontSize: 14.5, cursor: deciding ? "default" : "pointer" }}
            >{deciding ? "Recording..." : "I confirm"}</button>
            <button onClick={() => setConfirmingDecline(true)} disabled={deciding}
              style={{ flex: 1, background: "transparent", color: "#6b7280", border: "1px solid #d1d5db", padding: "13px 0", borderRadius: 8, fontWeight: 600, fontSize: 13.5, cursor: deciding ? "default" : "pointer" }}
            >I do not confirm</button>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", lineHeight: 1.6, marginBottom: 12 }}>
              Declining tells the compliance team that {data.completerName} is not authorised to act for the company. The team will be notified and the onboarding will not proceed on their authority. If something is simply unclear, add a note above instead.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => decide("decline")} disabled={deciding}
                style={{ flex: 1, background: deciding ? "#fca5a5" : "#dc2626", color: "#ffffff", border: "none", padding: "12px 0", borderRadius: 8, fontWeight: 700, fontSize: 13.5, cursor: deciding ? "default" : "pointer" }}
              >{deciding ? "Recording..." : "Yes, decline"}</button>
              <button onClick={() => setConfirmingDecline(false)} disabled={deciding}
                style={{ flex: 1, background: "transparent", color: "#374151", border: "1px solid #d1d5db", padding: "12px 0", borderRadius: 8, fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}
              >Go back</button>
            </div>
          </div>
        )}
        <div style={{ marginTop: 18, fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>
          Your response is recorded on the compliance file with the date, time and the address it was verified from. If you do not recognise this request, decline it and the compliance team will be notified.
        </div>
      </div>
    </div>
  );
}
